// IMPORTANT: this script has NO imports on purpose. Importing @theme/component or
// @theme/events triggers a dependency chain (component.js → utilities.js → events.js, etc.)
// that downloads serially. On cold sessions (incognito, first visit), the user can click
// "Add to cart" before the chain finishes resolving — so the cart-add listener attaches
// too late and the popup never opens. Keeping this file self-contained makes it load
// immediately after the HTML parses.

const CART_UPDATE_EVENT = 'cart:update';

/**
 * Module-level cart-add handling. Two purposes:
 *
 * 1. Tracks the most recent cart-add timestamp so `connectedCallback` can decide whether
 *    the element was just mounted as part of a cart-add flow (used for cases where the
 *    cart was previously empty and the element is created during morph AFTER the event fired).
 *
 * 2. After every cart-add, waits for the cart morph to settle and then directly nudges any
 *    cart-upsell currently in the DOM to open its popup. This is belt-and-suspenders: it works
 *    regardless of element lifecycle timing (newly mounted vs already mounted), making the
 *    "remove all → re-add" case reliable.
 */
let lastCartAddAt = 0;
const RECENT_ADD_WINDOW_MS = 3000;

// Take over the cart drawer's auto-open behavior. Without this, the drawer auto-opens
// synchronously on cart:update — visible for a fraction of a second before our popup
// can fire and close it. We want the popup to be the FIRST thing the customer sees on
// products that have popup-flagged pairings; for products without a popup, we open the
// drawer ourselves after the morph completes.
const cartDrawer = document.querySelector('cart-drawer-component');
const drawerWasAutoOpen = cartDrawer?.hasAttribute('auto-open') ?? false;
if (drawerWasAutoOpen && cartDrawer) {
  cartDrawer.removeAttribute('auto-open');
}

function openCartDrawerIfAuto() {
  if (drawerWasAutoOpen && cartDrawer && typeof (/** @type {any} */ (cartDrawer).open) === 'function') {
    /** @type {any} */ (cartDrawer).open();
  }
}

document.addEventListener(CART_UPDATE_EVENT, (event) => {
  const itemCount = /** @type {CustomEvent} */ (event)?.detail?.data?.itemCount;
  if (typeof itemCount !== 'number' || itemCount <= 0) return;

  lastCartAddAt = Date.now();

  // If the popup is currently open, the customer just accepted an upsell (clicked Add inside
  // the popup) or added a product from elsewhere. Either way: dismiss the popup and open the
  // drawer so they can review the updated cart.
  const openPopup = /** @type {HTMLElement | null} */ (document.querySelector('.cart-upsell__popup'));
  if (openPopup && !openPopup.hidden) {
    openPopup.hidden = true;
    openCartDrawerIfAuto();
    return;
  }

  // Normal flow: defer until after cart-items-component has morphed the new section content.
  // We poll at increasing intervals because morph speed varies (fast on cached repeat visits,
  // slower on cold incognito sessions). On the first attempt that finds the cart-upsell in
  // the DOM, we either open the popup OR open the drawer — never both.
  const attempts = [150, 400, 800, 1500];
  let resolved = false;
  attempts.forEach((delay, index) => {
    setTimeout(() => {
      if (resolved) return;

      const el = /** @type {any} */ (document.querySelector('cart-upsell'));
      const ready = el instanceof HTMLElement && el.children.length > 0 && typeof el.requestPopupOpen === 'function';

      if (ready && el.dataset.mode === 'pairings' && el.dataset.popup === 'true') {
        resolved = true;
        el.requestPopupOpen();
      } else if (ready || index === attempts.length - 1) {
        // cart-upsell is ready but no popup expected, OR we ran out of retries — open drawer.
        resolved = true;
        openCartDrawerIfAuto();
      }
    }, delay);
  });
});

function recentCartAdd() {
  return Date.now() - lastCartAddAt < RECENT_ADD_WINDOW_MS;
}

/**
 * <cart-upsell>
 *
 * Cart upsell controller. Server-side Liquid (snippets/cart-upsell.liquid) decides which mode
 * to render in via the `data-mode` attribute:
 *
 *   data-mode="pairings"  → pairings already rendered inline from the anchor product's
 *                            apis_tactical.upsell_pairings metafield. JS only handles
 *                            the popup (if data-popup="true") and the cart-update lifecycle.
 *
 *   data-mode="fallback"  → no pairings linked. JS fetches Shopify's native Search & Discovery
 *                            recommendations via the Section Rendering API and injects them.
 *
 * Discounts are NOT applied by this component — they're enforced natively by Shopify
 * automatic discounts (admin → Discounts) keyed to the recommended product.
 *
 * Popup mode is additive: inline rendering always happens; if data-popup="true", the same
 * content is also cloned into a centered <dialog> opened on user-initiated cart-add events.
 */
class CartUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {AbortController} */
  #lifecycleController = new AbortController();

  /** @type {string | null} */
  #lastFetchKey = null;

  /** @type {Map<string, string>} */
  #cache = new Map();

  /** @type {HTMLDialogElement | null} */
  #dialog = null;

  connectedCallback() {
    document.addEventListener(CART_UPDATE_EVENT, this.#handleCartUpdate, {
      signal: this.#lifecycleController.signal,
    });

    // On mount, open popup if a cart-add just happened (covers the empty-cart → first-add case
    // where this element is created during the morph AFTER the cart:update event already fired).
    this.#initialize({ mayOpenPopup: recentCartAdd() });
  }

  disconnectedCallback() {
    this.#fetchController?.abort();
    this.#lifecycleController.abort();
    this.#dialog?.remove();
    this.#dialog = null;
  }

  /**
   * cart:update fires on any cart change. cart-items-component then morphs the cart section,
   * which re-renders snippets/cart-upsell.liquid with the new anchor. After morph, this
   * element's data-* attrs and inner content reflect the new state; we just re-init.
   * @param {Event} event
   */
  #handleCartUpdate = (event) => {
    // Popup should fire on cart-adds (itemCount > 0), not on quantity updates or removals.
    const itemCount = /** @type {CustomEvent} */ (event)?.detail?.data?.itemCount;
    const isAdd = typeof itemCount === 'number' && itemCount > 0;
    // Defer to the next frame so the morph has finished updating our element first.
    requestAnimationFrame(() => this.#initialize({ mayOpenPopup: isAdd }));
  };

  /**
   * @param {{ mayOpenPopup: boolean }} options
   */
  #initialize({ mayOpenPopup }) {
    const mode = this.dataset.mode;
    const wantsPopup = this.dataset.popup === 'true';

    if (mode === 'pairings') {
      const okToShow = wantsPopup && mayOpenPopup;
      if (okToShow) {
        this.#openPopup();
      } else if (!wantsPopup) {
        this.#closeDialog();
      }
      return;
    }

    if (mode === 'fallback') {
      this.#fetchFallback();
      return;
    }

    // Unknown mode (or element empty) — hide.
    this.hidden = true;
  }

  async #fetchFallback() {
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId || 'cart-drawer-recommendations';
    const limit = Number(this.dataset.limit || 2);

    if (!productId) {
      this.hidden = true;
      return;
    }

    const key = `${productId}|${sectionId}|${limit}`;
    if (this.#lastFetchKey === key && this.children.length > 0) {
      // Same anchor as last render and content is already there — no work to do.
      return;
    }

    try {
      const html = await this.#fetchSectionHtml({ productId, sectionId, limit });
      if (this.#hasContent(html)) {
        this.innerHTML = html;
        this.hidden = false;
        this.#lastFetchKey = key;
      } else {
        this.replaceChildren();
        this.hidden = true;
      }
    } catch (err) {
      if (err?.name !== 'AbortError') {
        console.error('cart-upsell: fallback fetch failed', err);
      }
      this.replaceChildren();
      this.hidden = true;
    }
  }

  /**
   * Fetches the cart-drawer-recommendations section via the Section Rendering API.
   * Shopify populates the `recommendations` Liquid object when the URL hits /recommendations/products.
   * @param {{ productId: string, sectionId: string, limit: number }} params
   * @returns {Promise<string>}
   */
  async #fetchSectionHtml({ productId, sectionId, limit }) {
    const cacheKey = `${productId}|${sectionId}|${limit}`;
    const cached = this.#cache.get(cacheKey);
    if (cached !== undefined) return cached;

    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}recommendations/products?section_id=${encodeURIComponent(sectionId)}&product_id=${encodeURIComponent(
      productId
    )}&limit=${encodeURIComponent(String(limit))}&intent=related`;

    const response = await fetch(url, { signal: this.#fetchController.signal });
    if (!response.ok) throw new Error(`cart-upsell: ${response.status} at ${url}`);

    const text = await response.text();
    this.#cache.set(cacheKey, text);
    return text;
  }

  /**
   * Quick check whether the SRA response contains any product cards.
   * The fallback section renders elements with class cart-upsell__item.
   * @param {string} html
   */
  #hasContent(html) {
    return typeof html === 'string' && html.includes('cart-upsell__item');
  }

  /**
   * Clones the current upsell content into a centered <dialog> appended to <body>.
   * Lazy-creates the dialog on first use.
   */
  /**
   * Public method — called by the module-level cart-add listener after every cart-add,
   * so the popup opens reliably even when the element was just mounted during a cart morph.
   * Safe to call multiple times; #openPopup is a no-op if the dialog is already open.
   */
  requestPopupOpen() {
    this.#openPopup();
  }

  #openPopup() {
    if (this.children.length === 0) return;

    if (!this.#dialog) {
      // Clone the server-rendered template (which has the real icon-close.svg inlined).
      const template = document.getElementById('cart-upsell-popup-template');
      if (!(template instanceof HTMLTemplateElement)) {
        console.warn('cart-upsell: popup template missing');
        return;
      }
      const fragment = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
      const popup = /** @type {HTMLElement | null} */ (fragment.querySelector('.cart-upsell__popup'));
      if (!popup) return;

      popup.querySelectorAll('[data-cart-upsell-close]').forEach((btn) => {
        btn.addEventListener('click', () => this.#closeDialog());
      });

      document.body.appendChild(popup);
      this.#dialog = popup;
    }

    const content = this.#dialog.querySelector('.cart-upsell__popup-content');
    if (content) {
      // Mirror the cart-upsell's layout modifier on the popup content so horizontal/vertical
      // CSS selectors apply inside the popup (cart-upsell--row/--column live on <cart-upsell>,
      // not on its children).
      content.classList.remove('cart-upsell--row', 'cart-upsell--column');
      if (this.classList.contains('cart-upsell--row')) {
        content.classList.add('cart-upsell--row');
      } else {
        content.classList.add('cart-upsell--column');
      }
      content.innerHTML = this.innerHTML;
    }

    // Close the cart drawer's native <dialog> directly to remove it from the browser's top
     // layer. The drawer's backdrop is rendered in the top layer which sits ABOVE any z-index
     // we can set on our popup div — so the popup is invisible while the drawer is open.
     // Native close() removes from top layer synchronously (skipping the wrapper's slide-out
     // animation), so the popup becomes visible immediately.
    const cartDrawerDialog = /** @type {HTMLDialogElement | null} */ (
      document.querySelector('cart-drawer-component dialog')
    );
    if (cartDrawerDialog?.open) {
      cartDrawerDialog.close();
    }

    this.#dialog.hidden = false;
  }

  #closeDialog() {
    if (this.#dialog) {
      this.#dialog.hidden = true;
    }

    // After popup is dismissed, open the cart drawer so the customer can review their cart
    // and proceed to checkout (or continue shopping).
    openCartDrawerIfAuto();
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
